<template>
  <div id="outsideContainer" class="border word-cloze-page">
    <div class="set-location card-header">
      <div class="inside-header-left">
        <span class="andover-logo-area" @click="goList">
          <img style="width: 20px; height: 20px" src="../andover.png" alt="home" />
        </span>
      </div>
      <div class="navi-text">
        <span>{{ testItem?.subjectName || '选词填空' }}</span>
        <span v-if="testItem?.setName"> &gt; {{ testItem.setName }}</span>
        <span v-if="testItem?.isNirvanaSession"> &gt; Nirvana</span>
      </div>
    </div>

    <div class="card-wrapper">
      <div class="card-content cloze-content">
        <div v-if="loadError" class="cloze-panel">
          <p>{{ loadError }}</p>
          <el-button type="primary" @click="goList">返回列表</el-button>
        </div>

        <div v-else-if="loading || !testItem" class="cloze-panel" v-loading="true">
          <p>加载练习结果...</p>
        </div>

        <div v-else class="summary-page">
          <div class="summary-header">
            <h2>练习结果</h2>
            <p class="summary-score">
              正确 {{ testItem.totalCorrect }} / {{ testItem.totalQuestions }}（{{ testItem.accuracyPercent }}%）
            </p>
            <p class="summary-time">
              总用时：{{ formatClozeDuration(testItem.totalElapsedSeconds) }}
              · {{ formatClozeDateTime(testItem.createdDate) }}
            </p>
          </div>

          <section
            v-for="(group, groupIndex) in testItem.groupResults || []"
            :key="'result-group-' + (group.id || groupIndex)"
            class="summary-group-section"
          >
            <h3 class="summary-group-title">
              第 {{ groupIndex + 1 }} 组
              <span class="summary-group-score">
                {{ group.correct }} / {{ group.total }} 正确
                <template v-if="group.timedOut"> · 超时</template>
              </span>
            </h3>

            <div
              v-for="slot in group.slots || []"
              :key="slot.id || `${groupIndex}-${slot.slotIndex}`"
              class="sentence-row"
              :class="slot.isCorrect ? 'row-correct' : 'row-wrong'"
            >
              <p class="slot-word-label">
                <span class="slot-base-word">{{ slot.baseWord }}</span>
              </p>
              <p class="sentence-line">
                <span>{{ slot.sentenceBefore }}</span>
                <span
                  class="cloze-blank"
                  :class="slot.isCorrect ? 'blank-filled blank-correct' : 'blank-filled blank-wrong'"
                >
                  <span
                    v-if="slot.isCorrect && hasFormChange(slot)"
                    class="blank-filled-word blank-result-form"
                  >
                    <span
                      v-for="(seg, segIndex) in wordFormSegments(slot)"
                      :key="segIndex"
                      :class="{ 'form-change-part': seg.changed }"
                    >{{ seg.text }}</span>
                  </span>
                  <span
                    v-else-if="blankDisplayText(slot)"
                    class="blank-filled-word blank-result-form"
                  >
                    {{ blankDisplayText(slot) }}
                  </span>
                  <span v-else class="blank-placeholder">______</span>
                </span>
                <span>{{ slot.sentenceAfter }}</span>
              </p>

              <div class="result-detail">
                <el-icon v-if="slot.isCorrect" class="icon-correct"><CircleCheckFilled /></el-icon>
                <template v-else>
                  <el-icon class="icon-wrong"><CircleCloseFilled /></el-icon>
                  <span class="correct-answer-hint">
                    正确答案：<strong>{{ slot.baseWord }}</strong>
                  </span>
                </template>
              </div>
            </div>
          </section>

          <div class="summary-actions">
            <el-button type="primary" @click="goList">返回列表</el-button>
            <el-button
              v-if="isAssignmentResult"
              type="warning"
              :disabled="!hasWrongItems || assigningNirvana"
              :loading="assigningNirvana"
              @click="assignNirvanaFromWrongItems"
            >
              Nirvana
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import {
  diffWordFormSegments,
  formatClozeDateTime,
  formatClozeDuration,
  getPrimaryBaseWord,
  DEFAULT_CLOZE_TIME_LIMIT_SECONDS,
  collectWrongItemsFromGroups,
  nirvanaSetName,
  buildClozeGroups,
  buildVocClozeAssignmentPayload
} from './wordClozeUtils'

export default {
  name: 'ClozeResult',
  data () {
    return {
      loading: true,
      loadError: '',
      testItem: null,
      assigningNirvana: false
    }
  },
  computed: {
    isAssignmentResult () {
      return (
        Number(this.$route.query.assignmentResultId) > 0 ||
        Number(this.testItem?.assignmentId) > 0
      )
    },
    hasWrongItems () {
      return this.collectWrongItems().length > 0
    }
  },
  mounted () {
    this.loadResult()
  },
  methods: {
    formatClozeDuration,
    formatClozeDateTime,

    loadResult () {
      const assignmentResultId = Number(this.$route.query.assignmentResultId)
      const id = Number(this.$route.query.id)
      if (Number.isFinite(assignmentResultId) && assignmentResultId > 0) {
        this.fetchResult('VocClozeAssignmentResult/getById', assignmentResultId)
        return
      }
      if (!Number.isFinite(id) || id <= 0) {
        this.loadError = '未找到练习记录，请从 Cloze 或 Assignment 列表进入。'
        this.loading = false
        return
      }
      this.fetchResult('VocStudentClozeTest/getById', id)
    },

    fetchResult (action, id) {
      this.loading = true
      this.$store
        .dispatch(action, id)
        .then((response) => {
          this.testItem = response?.data || null
          if (!this.testItem) {
            this.loadError = '未找到该次选词填空结果。'
          }
        })
        .catch((err) => {
          console.error(err)
          const d = err.response?.data
          this.loadError =
            (typeof d === 'string' && d) ||
            d?.message ||
            d?.title ||
            err.message ||
            '加载选词填空结果失败'
          ElMessage.error(this.loadError)
        })
        .finally(() => {
          this.loading = false
        })
    },

    blankDisplayText (slot) {
      if (slot.isCorrect) return slot.correctForm
      return slot.studentAnswer || null
    },

    hasFormChange (slot) {
      const primary = getPrimaryBaseWord(slot.baseWord)
      return primary.toLowerCase() !== String(slot.correctForm || '').toLowerCase()
    },

    wordFormSegments (slot) {
      return diffWordFormSegments(slot.baseWord, slot.correctForm)
    },

    collectWrongItems () {
      return collectWrongItemsFromGroups(this.testItem?.groupResults)
    },

    assignNirvanaFromWrongItems () {
      const wrongItems = this.collectWrongItems()
      if (!wrongItems.length) {
        ElMessage({ message: '全部正确，无需 Nirvana', type: 'success' })
        return
      }
      const groupSize = Number(this.testItem?.groupSize) || 5
      const groups = buildClozeGroups(wrongItems, groupSize)
      const questionCount = groups.reduce((sum, group) => sum + group.slots.length, 0)
      const studentId =
        Number(this.testItem?.studentId) ||
        Number(localStorage.getItem('targetId')) ||
        Number(localStorage.getItem('userId')) ||
        0
      const teacherId = Number(localStorage.getItem('userId')) || studentId
      const subjectName = this.testItem?.subjectName || ''
      const setName = nirvanaSetName(this.testItem?.setName)
      const defaultSeconds =
        Number(this.testItem?.timeLimitSeconds) || DEFAULT_CLOZE_TIME_LIMIT_SECONDS

      ElMessageBox.prompt(
        `将本次 ${questionCount} 道错题重新布置为 Nirvana（共 ${groups.length} 组）。请填写每组限时（秒）。`,
        'Nirvana',
        {
          confirmButtonText: '布置',
          cancelButtonText: '取消',
          inputValue: String(defaultSeconds),
          inputPlaceholder: '每组限时（秒）',
          inputType: 'number',
          inputValidator: (value) => {
            const seconds = Number(value)
            if (!Number.isFinite(seconds) || seconds < 1) {
              return '请输入每组限时，至少 1 秒'
            }
            return true
          }
        }
      )
        .then(({ value }) => {
          this.assigningNirvana = true
          const timeLimitSeconds = Math.max(1, Math.round(Number(value)))
          const postData = buildVocClozeAssignmentPayload({
            payload: {
              subjectName,
              setName,
              type: this.testItem?.type || '选词填空'
            },
            groups,
            studentId,
            teacherId,
            groupSize,
            timeLimitSeconds
          })
          return this.$store.dispatch('VocClozeAssignment/add', postData)
        })
        .then((response) => {
          if (!response) return
          const assignmentId = Number(response?.data?.id ?? response?.data)
          ElMessage.success(
            Number.isFinite(assignmentId) && assignmentId > 0
              ? `Nirvana 作业已布置（ID ${assignmentId}）`
              : 'Nirvana 作业已布置'
          )
          if (Number.isFinite(assignmentId) && assignmentId > 0 && this.isStudentUser()) {
            this.$router.push({
              path: '/wordCloze',
              query: { assignmentId: String(assignmentId) }
            })
            return
          }
          this.$router.push({ path: '/clozeassignment' })
        })
        .catch((err) => {
          if (err === 'cancel' || err === 'close') return
          console.error(err)
          const d = err.response?.data
          const firstValidationError = d?.errors
            ? Object.values(d.errors).find((arr) => Array.isArray(arr) && arr.length)?.[0]
            : ''
          const msg =
            (typeof d === 'string' && d) ||
            firstValidationError ||
            d?.message ||
            d?.title ||
            err.message ||
            '布置 Nirvana 作业失败'
          ElMessage.error(String(msg))
        })
        .finally(() => {
          this.assigningNirvana = false
        })
    },

    isStudentUser () {
      return (localStorage.getItem('userRole') || '') === 'Student'
    },

    goList () {
      if (this.$route.query.assignmentResultId) {
        this.$router.push({ path: '/clozeassignment' })
        return
      }
      this.$router.push({ path: '/studentcloze' })
    }
  }
}
</script>

<style scoped>
.word-cloze-page {
  min-height: 100vh;
  background: #f6f7fb;
  color: #222;
}

.card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eee;
}

.navi-text {
  margin-left: 1rem;
  color: #666;
  font-size: 0.95rem;
}

.andover-logo-area {
  cursor: pointer;
}

.cloze-content {
  max-width: 820px;
  margin: 0 auto;
  padding: 1.5rem;
}

.cloze-panel {
  text-align: center;
  padding: 3rem 1rem;
}

.summary-page {
  text-align: left;
}

.summary-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.summary-score {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.summary-time {
  color: #888;
  font-size: 0.9rem;
}

.summary-group-section {
  margin-bottom: 1.5rem;
}

.summary-group-title {
  font-size: 1rem;
  margin-bottom: 0.75rem;
}

.summary-group-score {
  margin-left: 0.5rem;
  font-weight: 500;
  color: #888;
  font-size: 0.9rem;
}

.slot-word-label {
  margin: 0 0 0.35rem;
}

.slot-base-word {
  font-weight: 700;
  color: deepskyblue;
}

.sentence-row {
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  background: #fafafa;
  border: 1px solid #eee;
}

.sentence-row.row-correct {
  border-color: #81c784;
  background: #f1f8e9;
}

.sentence-row.row-wrong {
  border-color: #ef9a9a;
  background: #fff5f5;
}

.sentence-line {
  margin: 0;
  line-height: 1.8;
  font-size: 1.05rem;
}

.cloze-blank {
  display: inline-block;
  min-width: 6rem;
  margin: 0 0.2rem;
  padding: 0.1rem 0.5rem;
  border-bottom: 2px dashed #bbb;
  vertical-align: baseline;
  text-align: center;
}

.cloze-blank.blank-filled {
  border-bottom-style: solid;
}

.cloze-blank.blank-correct {
  border-bottom-color: #43a047;
  background: rgba(67, 160, 71, 0.1);
}

.cloze-blank.blank-wrong {
  border-bottom-color: #e53935;
  background: rgba(229, 57, 53, 0.08);
}

.blank-placeholder {
  color: #aaa;
  letter-spacing: 2px;
}

.blank-filled-word {
  color: deepskyblue;
  font-weight: 700;
}

.blank-result-form {
  cursor: default;
}

.form-change-part {
  color: #d32f2f;
  font-weight: 800;
  background: rgba(211, 47, 47, 0.12);
  border-radius: 3px;
  padding: 0 2px;
}

.result-detail {
  margin-top: 0.4rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.icon-correct {
  color: #43a047;
}

.icon-wrong {
  color: #e53935;
}

.correct-answer-hint {
  color: #c62828;
  font-size: 0.9rem;
}

.summary-actions {
  text-align: center;
  margin-top: 1.5rem;
}
</style>
